import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import publicApplicationsService from '../services/publicApplicationsService';

const CVViewerModal = ({ open, onClose, applicationId, applicantName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cvUrl, setCvUrl] = useState(null);
  const [filename, setFilename] = useState('cv.pdf');
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    if (open && applicationId) {
      loadCVUrl();
    }
  }, [open, applicationId]);

  const loadCVUrl = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await publicApplicationsService.getCVUrl(applicationId);
      if (response.success) {
        setCvUrl(response.url);
        setFilename(response.filename || 'cv.pdf');
      } else {
        setError('Failed to load CV');
      }
    } catch (err) {
      console.error('Error loading CV:', err);
      setError('Failed to load CV. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (cvUrl) {
      const link = document.createElement('a');
      link.href = cvUrl;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const handleFullscreen = () => {
    if (cvUrl) {
      window.open(cvUrl, '_blank');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: '90vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: '#29204C', color: 'white', py: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            CV - {applicantName}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: 'calc(90vh - 140px)', overflow: 'hidden' }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            p={3}
          >
            <Alert severity="error" sx={{ width: '100%', maxWidth: 500 }}>
              {error}
            </Alert>
          </Box>
        ) : (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              overflow: 'auto',
              bgcolor: '#f5f5f5',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              p: 2,
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s ease',
              }}
            >
              <iframe
                src={cvUrl}
                title="CV Viewer"
                style={{
                  width: '100%',
                  height: '80vh',
                  border: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  backgroundColor: 'white',
                }}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box display="flex" gap={1}>
            <Button
              size="small"
              startIcon={<ZoomOutIcon />}
              onClick={handleZoomOut}
              disabled={loading || error || zoom <= 50}
            >
              Zoom Out
            </Button>
            <Typography variant="body2" sx={{ alignSelf: 'center', mx: 1, minWidth: 50, textAlign: 'center' }}>
              {zoom}%
            </Typography>
            <Button
              size="small"
              startIcon={<ZoomInIcon />}
              onClick={handleZoomIn}
              disabled={loading || error || zoom >= 200}
            >
              Zoom In
            </Button>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<FullscreenIcon />}
              onClick={handleFullscreen}
              disabled={loading || error}
            >
              Open in New Tab
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={loading || error}
              sx={{ bgcolor: '#29204C', '&:hover': { bgcolor: '#1a1333' } }}
            >
              Download
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CVViewerModal;
